import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { X } from "lucide-react";
import { articlesAPI, tagsAPI } from "../services/api";
import Breadcrumbs from "../components/Breadcrumbs";
import ApprovedTagChips from "../components/ApprovedTagChips";
import "./CreateContentForm.css";
import { useToasts } from "../hooks/useToasts";
import { useFolders } from "../context/FoldersContext";
import Button from "../components/Button";
import Form from "../components/Form";
import PdfImportPanel from "../components/PdfImportPanel";
import {
  getEffectiveBodyLimit,
  canUsePdfImport,
} from "../constants/contentLimits";
import { isEditorialUser as isEditorialAccount } from "../constants/editorialAccounts";
import { useAuthState } from "../hooks/useAuth";

const EDITORIAL_LESSON_TAG_LIMIT = 2;

export default function CreateLesson() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const folderFromUrl = searchParams.get("folder");
  const contentType = "lesson";
  const [formData, setFormData] = useState({
    title: folderFromUrl || "",
    author: "",
    chapter: "",
    body: "",
    tags: [],
  });
  const [folders, setFolders] = useState([]);
  const [selectedFolderPath, setSelectedFolderPath] = useState("");
  const [authors, setAuthors] = useState([]);
  const [approvedTags, setApprovedTags] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showAuthorDropdown, setShowAuthorDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const authorDropdownRef = useRef(null);
  const { addToast } = useToasts();
  const { invalidateCache } = useFolders();
  const { user } = useAuthState();
  const lessonBodyLimit = getEffectiveBodyLimit(contentType, user);
  const showPdfImport = canUsePdfImport(user);
  const isEditorialUser = isEditorialAccount(user);

  useEffect(() => {
    const loadFolders = async () => {
      try {
        const allArticles = await articlesAPI.getMyArticles();
        const contentFolderPaths = Array.from(
          new Set(
            allArticles.data.articles
              .filter(
                (article) =>
                  article.title &&
                  article.body !== "Folder placeholder" &&
                  article.body !== "Subfolder placeholder",
              )
              .map((article) => article.title),
          ),
        );

        const leafFolders = contentFolderPaths
          .filter(
            (folderPath) =>
              !contentFolderPaths.some(
                (candidatePath) => candidatePath.startsWith(`${folderPath} / `),
              ),
          )
          .sort((a, b) => a.localeCompare(b))
          .map((folderPath) => ({
            name: folderPath.includes(" / ")
              ? folderPath.split(" / ").pop()
              : folderPath,
            fullPath: folderPath,
          }));

        setFolders(leafFolders);

        // Extract unique authors and lessons
        const uniqueAuthors = [...new Set(
          allArticles.data.articles
            .map(a => a.author)
            .filter(author => author && author.trim() !== '')
        )];
        setAuthors(uniqueAuthors);
      } catch (err) {
        console.error("Failed to load folders", err);
      }
    };
    loadFolders();
  }, []);

  useEffect(() => {
    if (!isEditorialUser) {
      setApprovedTags([]);
      return;
    }

    let isMounted = true;

    const loadApprovedTags = async () => {
      try {
        const response = await tagsAPI.getApprovedTags("lesson");
        if (isMounted) {
          setApprovedTags(response.data?.tags || []);
        }
      } catch {
        if (isMounted) {
          setApprovedTags([]);
        }
      }
    };

    loadApprovedTags();

    return () => {
      isMounted = false;
    };
  }, [isEditorialUser]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        if (folderFromUrl) {
          navigate(`/study/lessons?folder=${encodeURIComponent(folderFromUrl)}`);
        } else {
          navigate("/create-content");
        }
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [navigate, folderFromUrl]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
      if (authorDropdownRef.current && !authorDropdownRef.current.contains(event.target)) {
        setShowAuthorDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const buildBreadcrumbs = () => {
    const breadcrumbs = [
      { label: 'MySpace', path: '/myspace' },
      { label: 'Classes', path: '/study/lessons' }
    ];
    
    if (folderFromUrl) {
      const parts = folderFromUrl.split(' / ');
      parts.forEach((part, index) => {
        const path = parts.slice(0, index + 1).join(' / ');
        breadcrumbs.push({
          label: part,
          path: `/study/lessons?folder=${encodeURIComponent(path)}`
        });
      });
    }
    
    breadcrumbs.push({ label: 'Create Lesson', path: null });
    return breadcrumbs;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "title") {
      setSelectedFolderPath("");
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTagToggle = (tagSlug) => {
    setFormData((prev) => {
      const currentTags = Array.isArray(prev.tags) ? prev.tags : [];

      if (currentTags.includes(tagSlug)) {
        return {
          ...prev,
          tags: currentTags.filter((tag) => tag !== tagSlug),
        };
      }

      if (currentTags.length >= EDITORIAL_LESSON_TAG_LIMIT) {
        addToast(
          `Select up to ${EDITORIAL_LESSON_TAG_LIMIT} tags`,
          "error-warning",
        );
        return prev;
      }

      return {
        ...prev,
        tags: [...currentTags, tagSlug],
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      addToast("Title is required for folder organization", "error-warning");
      return;
    }

    const normalizeString = (str) => str.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const toTitleCase = (str) => str.trim().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
    
    const normalizedInput = normalizeString(formData.title.trim());
    const existingFolder = selectedFolderPath
      ? folders.find((folder) => folder.fullPath === selectedFolderPath)
      : folders.find((folder) => normalizeString(folder.name) === normalizedInput);
    const finalTitle = existingFolder ? existingFolder.fullPath : toTitleCase(formData.title.trim());

    try {
      // Check if folder exists, if not create placeholder
      const folderExists = Boolean(existingFolder);
      
      if (!folderExists) {
        await articlesAPI.createArticle({
          title: finalTitle,
          body: "Folder placeholder",
          contentType: "lesson",
        });
      }
      
      await articlesAPI.createArticle({
        title: finalTitle,
        author: formData.author,
        chapter: formData.chapter,
        body: formData.body,
        contentType: contentType,
        tags: isEditorialUser ? formData.tags : [],
      });
      setFormData({ title: "", author: "", chapter: "", body: "", tags: [] });
      invalidateCache();
      addToast(`Lesson saved`, "success-info");
    } catch (err) {
      const errorMsg =
        err?.response?.data?.errors?.[0]?.msg ||
        err?.response?.data?.message ||
        "Failed to create lesson. Please try again.";
      addToast(errorMsg, "error-warning");
    }
  };

  return (
    <div className="form-content-frame form-content-frame-fullscreen">
      <Breadcrumbs path={buildBreadcrumbs()} />
      <button
        onClick={() => folderFromUrl ? navigate(`/study/lessons?folder=${encodeURIComponent(folderFromUrl)}`) : navigate("/create-content")}
        className="createcontent-close-btn"
        title="Back to Create Content"
      >
        <X size={32} />
      </button>
      <div className="createcontent-spacer-lg" />
      <div className="createcontent-form-layout">
        <Form
          onSubmit={handleSubmit}
          showCancel={false}
          submitText="Save"
          extraButtons={
            <Button
              className="button-btn button-btn-red"
              onClick={() => folderFromUrl ? navigate(`/study/lessons?folder=${encodeURIComponent(folderFromUrl)}`) : navigate("/create-content")}
              type="button"
            >
              Cancel
            </Button>
          }
          containerClassName="form-createcontent-double"
        >
          <div className="createcontent-form-row">
            {!folderFromUrl && (
              <div className="createcontent-form-group" ref={dropdownRef}>
                <label htmlFor="title">Folder Title* (or select existing folder)</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  onFocus={() => setShowDropdown(true)}
                  placeholder="Type to see existing folders or create new"
                  maxLength="80"
                  required
                  autoComplete="off"
                />
                {showDropdown && folders.length > 0 && (
                  <div className="createcontent-dropdown">
                    {folders.map((folder) => (
                      <div
                        key={folder.fullPath}
                        onClick={() => {
                          setFormData((prev) => ({ ...prev, title: folder.name }));
                          setSelectedFolderPath(folder.fullPath);
                          setShowDropdown(false);
                        }}
                        className="createcontent-dropdown-item"
                      >
                        {folder.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="createcontent-form-group">
              <label htmlFor="chapter">Lesson Title*</label>
              <input
                type="text"
                id="chapter"
                name="chapter"
                value={formData.chapter}
                onChange={handleChange}
                placeholder="Required"
                maxLength="50"
                required
                autoComplete="off"
              />
            </div>
            <div className="createcontent-form-group" ref={authorDropdownRef}>
              <label htmlFor="author">Author</label>
              <input
                type="text"
                id="author"
                name="author"
                value={formData.author}
                onChange={handleChange}
                onFocus={() => setShowAuthorDropdown(true)}
                placeholder="Optional"
                maxLength="50"
                autoComplete="off"
              />
              {showAuthorDropdown && authors.length > 0 && (
                <div className="createcontent-dropdown">
                  {authors.map((author) => (
                    <div
                      key={author}
                      onClick={() => {
                        setFormData((prev) => ({ ...prev, author }));
                        setShowAuthorDropdown(false);
                      }}
                      className="createcontent-dropdown-item"
                    >
                      {author}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          {isEditorialUser && approvedTags.length > 0 && (
            <div className="createcontent-form-group createcontent-tags-group">
              <label>Tags</label>
              <ApprovedTagChips
                tags={approvedTags}
                selectedSlugs={formData.tags}
                onTagClick={handleTagToggle}
                maxSelected={EDITORIAL_LESSON_TAG_LIMIT}
              />
            </div>
          )}
          <div className="createcontent-form-group">
            <div className="createcontent-body-header">
              <label htmlFor="body">Body*</label>
              {showPdfImport && (
                <PdfImportPanel
                  currentText={formData.body}
                  onImportText={(nextBody) =>
                    setFormData((prev) => ({ ...prev, body: nextBody }))
                  }
                  maxLength={lessonBodyLimit}
                  placement="inline"
                />
              )}
            </div>
            <textarea
              id="body"
              name="body"
              value={formData.body}
              onChange={handleChange}
              required
              placeholder="Required"
              rows="6"
              maxLength={lessonBodyLimit ?? undefined}
            ></textarea>
            <span className="createcontent-pdf-hint">
              {lessonBodyLimit
                ? `${formData.body.length}/${lessonBodyLimit} characters`
                : `${formData.body.length} characters`}
            </span>
          </div>
          <div className="createcontent-spacer-md" />
        </Form>
      </div>
    </div>
  );
}
